/**
 * Debounced session search against the inbox search API (view=search-all).
 */

const MIN_QUERY_LEN = 2;
const DEBOUNCE_MS = 300;

export class SessionSearchQueryController {
  constructor({ searchFn, debounceMs = DEBOUNCE_MS } = {}) {
    this._searchFn = searchFn;
    this._debounceMs = debounceMs;
    this._timer = null;
    this._generation = 0;
  }

  minQueryLength() {
    return MIN_QUERY_LEN;
  }

  normalizeQuery(raw) {
    return String(raw ?? "").trim();
  }

  isQueryable(raw) {
    return this.normalizeQuery(raw).length >= MIN_QUERY_LEN;
  }

  cancel() {
    if (this._timer != null) {
      window.clearTimeout(this._timer);
      this._timer = null;
    }
    this._generation += 1;
  }

  /**
   * @param {string} raw
   * @param {(payload: { data: unknown[], nextCursor: string | null }) => void} onResult
   * @param {(err: Error) => void} onError
   */
  schedule(raw, onResult, onError) {
    this.cancel();
    const q = this.normalizeQuery(raw);
    if (q.length < MIN_QUERY_LEN) {
      onResult({ data: [], nextCursor: null });
      return;
    }
    const generation = this._generation;
    this._timer = window.setTimeout(() => {
      this._timer = null;
      void this._run(generation, q, onResult, onError);
    }, this._debounceMs);
  }

  async loadMore(raw, cursor, onResult, onError) {
    const q = this.normalizeQuery(raw);
    if (q.length < MIN_QUERY_LEN || !cursor) return;
    const generation = this._generation;
    try {
      const page = await this._searchFn({ q, cursor });
      if (generation !== this._generation) return;
      onResult(page);
    } catch (err) {
      if (generation !== this._generation) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  async _run(generation, q, onResult, onError) {
    try {
      const page = await this._searchFn({ q, cursor: null });
      if (generation !== this._generation) return;
      onResult(page);
    } catch (err) {
      if (generation !== this._generation) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  }
}
